import React from 'react';
import { Table, Icon, Typography, Pagination, Popconfirm } from 'antd';

import './SalesList.scss';
import { ISale, SaleStatus } from '../../../models/sales-sf.models';
import { formatDateUtc } from '../../../utils/moment.functions';

const { Text } = Typography;

interface IProps {
    sales: ISale[],
    isLoading: boolean,
    page: number,
    pageSize: number,
    checkPermissions(permissions: string[]): boolean,
    idCoach?: string,
    total: number,
    onChangePage: (page: number) => void,
    onChangePageSize: (pageSize: number) => void,
    onEditSale: (sale: ISale) => void,
    handleCancelOrder: (orderId: string) => void,
};

const SalesList = ({
    sales,
    isLoading,
    page,
    pageSize,
    idCoach,
    total,
    onChangePage,
    onChangePageSize,
    onEditSale,
    handleCancelOrder,
 }: IProps) => {

    const columns = [
        {
            title: 'ID de pagamento',
            dataIndex: 'paymentId',
            key: 'paymentId',
        },
        {
            title: 'Nome',
            dataIndex: 'nome',
            key: 'nome',
            sorter: (a: ISale, b: ISale) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`),
            render: (_: any, record: ISale) => `${record.firstName} ${record.lastName}`,
        },
        {
            title: 'Produto',
            dataIndex: 'produto',
            key: 'produto',
            sorter: (a: ISale, b: ISale) => `${a.productCode} ${a.productType}`.localeCompare(`${b.productCode} ${b.productType}`),
            render: (_: any, record: ISale) => {
                const translateProductType = (type: string) => {
                    switch (type) {
                        case 'ANNUAL':
                            return 'Anual';
                        case 'RECURRENT':
                            return 'Recorrente';
                        default:
                            return type;
                    }
                };

                return `${record.productCode} / ${translateProductType(record.productType)}`;
            },
        },
        {
            title: 'Valor',
            dataIndex: 'paymentAmount',
            key: 'paymentAmount',
            sorter: (a: ISale, b: ISale) => a.paymentAmount > b.paymentAmount ? 1 : -1,
            render: (value: number) => {
                const formatToBRL = (amount: number) => {
                    return new Intl.NumberFormat('pt-BR', {
                        style: 'currency',
                        currency: 'BRL',
                    }).format(amount / 100);
                };

                return formatToBRL(value);
            },
        },
        {
            title: 'Data de criação',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (createdAt: any) => formatDateUtc(createdAt, 'DD/MM/YYYY [às] HH:mm:ss')
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            sorter: (a: ISale, b: ISale) => a.status > b.status ? 1 : -1,
        },
        {
            title: 'Ações',
            key: 'action',
            render: (_: any, record: ISale) => (
                <span className="icons-action">
                    {
                        !idCoach && (
                            <Icon
                                type="edit"
                                onClick={() => onEditSale(record)}
                            />
                        )
                    }
                    {
                        record.status === SaleStatus.CREATED && (
                            <Popconfirm
                                title={'Tem certeza que deseja cancelar essa ordem?'}
                                okText='Sim'
                                cancelText='Não'
                                onConfirm={() => handleCancelOrder(record.orderId)}
                                placement='left'
                            >
                                <Icon type="close-circle" />
                            </Popconfirm>
                        )
                    }
                </span>
            )
        },
    ];

    if (!idCoach) {
        columns.splice(1, 0,{
            title: 'E-mail',
            dataIndex: 'email',
            key: 'email',
            sorter: (a: ISale, b: ISale) => a.email > b.email ? 1 : -1,
        });
    }

    return (
        <>
            <Table
                loading={isLoading}
                columns={columns}
                className={"table-sales"}
                dataSource={sales.map((sale: ISale) => ({ ...sale, key: `${sale.orderId}-${sale.paymentId}`}))}
                pagination={idCoach ? undefined : false}
            />
            {
                !idCoach && (
                    <>
                        <div style={{ textAlign: 'right', marginTop: 10, marginBottom: -15 }}>
                            <Text><b>{total}</b> vendas encontradas</Text>
                        </div>
                        <Pagination
                            className="all-sales-pagination"
                            showSizeChanger
                            onShowSizeChange={(_, pageSize) => {
                                onChangePageSize(pageSize);
                                onChangePage(1);
                            }}
                            defaultPageSize={pageSize}
                            defaultCurrent={page}
                            current={page}
                            total={total}
                            pageSizeOptions={['10', '25', '50', '100']}
                            onChange={page => onChangePage(page)}
                        />
                    </>
                )
            }
        </>
    );
};

export default SalesList;
